import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subscription } from 'rxjs';
import { environment } from '../../../../../../../environments/environment';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { TipoContatoEnum } from '@shared/enums/pessoa/tipo-contato.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MaskService } from '@shared/services/mask.service';
import { FinancasLookupService } from '@shared/services/apis/financas/financas-lookup.service';
import { FinancasLookupItem } from '@shared/services/responses/financas/lookup-online-select.response';
import type { KanbanStage } from '../../../processo-compras.models';
import {
  calcularSubtotalItemPedido,
  calcularTotalPedido,
  CardProduct,
  numToBR,
  parseBR,
  PedidoItemView,
  PedidoState,
  ProductSearchResult,
} from '../card-edit-dialog.types';
import { CedBuscaProdutoComponent } from '../panels/ced-busca-produto.component';
import { CedEnvioPedidoComponent, EnviarPedidoPayload } from './ced-envio-pedido.component';

/**
 * Etapa de Pedido de Compra: itens com tributos, condições comerciais, parcelas,
 * valores do pedido, contabilidade e observações. O envio ao fornecedor é o bloco
 * compartilhado ced-envio-pedido, com botão só quando a Aprovação está desativada.
 *
 * Contrato com o shell:
 * - `state` é o objeto PedidoState do shell (montado por montarPedidoState) e é editado
 *   AQUI por referência — espelho compartilhado; o doSave do shell lê o mesmo objeto,
 *   robusto contra a destruição do filho ao trocar de etapa.
 * - `allStages` já vem filtrado pelas etapas ATIVAS: a ausência de 'aprovacao' é o sinal
 *   de que o admin desativou a etapa.
 * - `(dirty)` marca alterações não salvas.
 * - `(enviarPedido)`: repassa o evento do bloco de envio; o shell salva e chama a API.
 * - Fornecedor e inclusão/exclusão de produtos (pedido que chega sem cotação): o fornecedor vai em
 *   `state.envio.idFornecedor` e é gravado no "Salvar" do card; `(produtoAdicionado)`/`(produtoRemovido)`
 *   mantêm os `products` do shell alinhados por índice com `state.itens`.
 * - Lookups de finanças (centro de custo / categoria DRE) são carregados pelo próprio filho.
 */
@Component({
  selector: 'app-ced-stage-pedido',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    AlertCardComponent,
    CedBuscaProdutoComponent,
    CedEnvioPedidoComponent,
    SelectPesquisavelComponent,
  ],
  templateUrl: './ced-stage-pedido.component.html',
  styleUrls: ['../ced-scrollbars.css', './ced-stage-pedido.component.css', '../ced-painel-inclusao.css'],
})
export class CedStagePedidoComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) state!: PedidoState;
  /** Etapas ATIVAS do quadro — usado só para saber se a Aprovação existe. */
  @Input() allStages: KanbanStage[] = [];
  /** Nome do fornecedor atual (resolvido pelo shell), para o select exibi-lo sem buscar. */
  @Input() nomeFornecedor = '';

  @Output() dirty = new EventEmitter<void>();
  @Output() enviarPedido = new EventEmitter<EnviarPedidoPayload>();
  @Output() fornecedorChange = new EventEmitter<{ idFornecedor: number; nome: string }>();
  @Output() produtoAdicionado = new EventEmitter<CardProduct>();
  @Output() produtoRemovido = new EventEmitter<number>();

  public urlFornecedor = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Fornecedor}`;
  fornecedorControl = new FormControl<number | null>(null);
  fornecedorOpcaoAtual: { id: number; descricao: string }[] = [];

  itemAdding = false;

  pedidoCentroCustoOpcoes: FinancasLookupItem[] = [];
  pedidoCategoriaDreOpcoes: FinancasLookupItem[] = [];

  parcelasAutomaticas = false;

  moedaMask: ReturnType<MaskService['getDynamicCurrencyMask']>;
  diasMask: ReturnType<MaskService['getIntegerMask']>;
  quantidadeMask: ReturnType<MaskService['getDynamicDecimalMask']>;

  private readonly subs = new Subscription();

  constructor(
    private financasLookupService: FinancasLookupService,
    private maskService: MaskService,
    private alertService: AlertService,
  ) {
    this.moedaMask = this.maskService.getDynamicCurrencyMask();
    this.diasMask = this.maskService.getIntegerMask();
    this.quantidadeMask = this.maskService.getDynamicDecimalMask();
  }

  ngOnInit(): void {
    this.carregarLookupsFinancas();
    this.inicializarDistribuicaoParcelas();
    this.inicializarFornecedor();
    this.state.itensRetratoEnvio ??= this.novoRetrato();
  }

  /** O shell resolve o nome do fornecedor depois de abrir o card: atualiza a opção exibida no select. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nomeFornecedor'] && !changes['nomeFornecedor'].firstChange) this.inicializarFornecedor();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Depois do envio o pedido já foi para aquele fornecedor: a troca fica bloqueada. */
  get fornecedorBloqueado(): boolean {
    return !!this.state.envio.datetimeEnvio;
  }

  /**
   * Pedido já enviado e itens diferentes do retrato (item incluído, removido ou valor alterado): o fornecedor
   * está com a versão antiga. O retrato vive em `state` (sobrevive à recriação da etapa ao abrir Anexos ou
   * Comentários) e é refeito quando o envio muda — o shell salva antes de enviar, então os itens atuais são
   * exatamente os enviados.
   */
  get isAlteradoAposEnvio(): boolean {
    const retrato = this.state.itensRetratoEnvio;
    if (!retrato || retrato.datetimeEnvio !== this.state.envio.datetimeEnvio) {
      this.state.itensRetratoEnvio = this.novoRetrato();
      return false;
    }
    if (!this.state.envio.datetimeEnvio) return false;
    const atuais = this.state.itens;
    return atuais.length !== retrato.itens.length
      || atuais.some((it, i) => this.assinaturaItem(it) !== retrato.itens[i]);
  }

  private novoRetrato(): { datetimeEnvio: string | null; itens: string[] } {
    return { datetimeEnvio: this.state.envio.datetimeEnvio, itens: this.state.itens.map(it => this.assinaturaItem(it)) };
  }

  /** Campos do item que o fornecedor recebe; `qty` e valores nulos são normalizados para não gerar falso aviso. */
  private assinaturaItem(it: PedidoItemView): string {
    return JSON.stringify([it.idCompraItem ?? null, it.sku, it.qty, it.unit, it.preco ?? null, it.ipi ?? null,
      it.st ?? null, it.ibs ?? null, it.cbs ?? null, it.desconto ?? null]);
  }

  private inicializarFornecedor(): void {
    const id = this.state.envio.idFornecedor;
    if (id) {
      this.fornecedorOpcaoAtual = [{ id, descricao: this.nomeFornecedor || `Fornecedor #${id}` }];
      this.fornecedorControl.setValue(id, { emitEvent: false });
    }
  }

  /** Grava só no estado do card: o fornecedor vai no "Salvar" junto com o resto do pedido. */
  onFornecedorSelecionado(sel: { id: number; descricao: string } | null): void {
    if (!sel || sel.id === this.state.envio.idFornecedor) return;
    this.state.envio.idFornecedor = sel.id;
    this.fornecedorChange.emit({ idFornecedor: sel.id, nome: sel.descricao });
    this.dirty.emit();
  }

  /** A busca vive dentro do @if do painel: abrir recria a busca vazia e já focada; fechar a descarta. */
  toggleAddingItem(): void {
    this.itemAdding = !this.itemAdding;
  }

  /** Só produto do cadastro: o Pedido não aceita item avulso. */
  addProductFromSearch(r: ProductSearchResult): void {
    this.state.itens = [...this.state.itens, {
      idCompraItem: null,
      sku: r.sku,
      name: r.name,
      qty: 1,
      unit: r.unit,
      stock: r.stock,
      preco: r.price,
      ipi: null,
      st: null,
      ibs: null,
      cbs: null,
      desconto: null,
    }];
    this.produtoAdicionado.emit({
      idProduto: r.idProduto ?? null,
      sku: r.sku,
      name: r.name,
      marca: r.marca ?? null,
      qty: 1,
      unit: r.unit,
      stock: r.stock,
      price: numToBR(r.price),
      valorUnitario: r.price,
      isAvulso: false,
    });
    this.itemAdding = false;
    this.onTotalPedidoChange();
  }

  removeItem(index: number): void {
    this.state.itens = this.state.itens.filter((_, i) => i !== index);
    this.produtoRemovido.emit(index);
    this.onTotalPedidoChange();
  }

  /** Aprovação desativada ⇒ o botão de envio volta para esta etapa. */
  get aprovacaoAtiva(): boolean {
    return this.allStages.some(s => s.key === 'aprovacao');
  }

  // ----- Proxies para o estado compartilhado (mantém o template idêntico ao pré-extração) -----
  get pedidoItens(): PedidoItemView[] { return this.state.itens; }
  get pedidoFormaPagamentoOpcoes(): string[] { return this.state.formaPagamentoOpcoes; }
  get pedidoFormaPagamento(): string { return this.state.formaPagamento; }
  set pedidoFormaPagamento(v: string) { this.state.formaPagamento = v; }
  /** A API ainda recebe texto, mas este campo só permite persistir dias inteiros não negativos. */
  get pedidoPrazoEntregaDias(): number | null {
    const prazo = this.state.prazoEntrega.trim();
    return /^\d+$/.test(prazo) ? Number(prazo) : null;
  }

  onPrazoEntregaChange(value: number | string | null): void {
    // A máscara é `nullable`, então limpar o campo emite null (e '' vira 0 no Number) — sem tratar
    // antes, o campo gravava "0" e ficava impossível de limpar, com o required satisfeito por zero.
    if (value == null || value === '') {
      this.state.prazoEntrega = '';
      this.dirty.emit();
      return;
    }

    const numero = typeof value === 'number' ? value : Number(value);
    this.state.prazoEntrega = Number.isFinite(numero) && numero >= 0
      ? String(Math.trunc(numero))
      : '';
    this.dirty.emit();
  }
  get pedidoFrete(): number | null { return this.state.frete; }
  set pedidoFrete(v: number | null) { this.state.frete = v; }
  get pedidoOutrasDespesas(): number | null { return this.state.outrasDespesas; }
  set pedidoOutrasDespesas(v: number | null) { this.state.outrasDespesas = v; }
  get pedidoCentroCusto(): number | null { return this.state.centroCusto; }
  set pedidoCentroCusto(v: number | null) { this.state.centroCusto = v; }
  get pedidoCategoriaDre(): number | null { return this.state.categoriaDre; }
  set pedidoCategoriaDre(v: number | null) { this.state.categoriaDre = v; }
  get pedidoObservacoes(): string { return this.state.observacoes; }
  set pedidoObservacoes(v: string) { this.state.observacoes = v; }
  get pedidoParcelas() { return this.state.parcelas; }

  addParcela(): void {
    const seq = this.state.parcelas.length + 1;
    this.state.parcelas = [...this.state.parcelas, { sequencia: seq, dias: null, valor: 0 }];
    if (seq === 1) this.parcelasAutomaticas = true;
    if (this.parcelasAutomaticas) this.distribuirTotalEntreParcelas();
    this.dirty.emit();
  }

  removeParcela(index: number): void {
    this.state.parcelas = this.state.parcelas
      .filter((_, i) => i !== index)
      .map((p, i) => ({ ...p, sequencia: i + 1 }));
    if (this.parcelasAutomaticas) this.distribuirTotalEntreParcelas();
    this.dirty.emit();
  }

  onTotalPedidoChange(): void {
    if (this.parcelasAutomaticas) this.distribuirTotalEntreParcelas();
    this.dirty.emit();
  }

  /**
   * Quantidade editável nesta etapa. Espelha a máscara sem clamp (o binding é one-way; corrigir o
   * valor aqui divergiria da tela) — quantidade zerada é barrada no salvamento, como na Solicitação.
   */
  onQuantidadeChange(it: PedidoItemView, value: number | string | null): void {
    it.qty = Number(value) || 0;
    this.onTotalPedidoChange();
  }

  onValorParcelaChange(): void {
    this.parcelasAutomaticas = false;
    this.dirty.emit();
  }

  distribuirValoresParcelas(): void {
    if (!this.state.parcelas.length) return;
    this.parcelasAutomaticas = true;
    this.distribuirTotalEntreParcelas();
    this.dirty.emit();
  }

  /**
   * Divide o total em centavos para impedir diferenças de arredondamento.
   * O eventual resíduo fica na última parcela, mantendo a soma idêntica ao pedido.
   */
  private distribuirTotalEntreParcelas(): void {
    const valores = this.calcularParcelasEmCentavos();
    if (!valores.length) return;

    this.state.parcelas = this.state.parcelas.map((parcela, index) => ({
      ...parcela,
      valor: valores[index] / 100,
    }));
  }

  /** Mantém automática uma distribuição vazia ou que ainda corresponde ao total atual. */
  private inicializarDistribuicaoParcelas(): void {
    if (!this.state.parcelas.length) return;

    const valoresAtuais = this.state.parcelas.map(parcela => Math.round(parseBR(parcela.valor) * 100));
    const todosZerados = valoresAtuais.every(valor => valor === 0);
    const valoresEsperados = this.calcularParcelasEmCentavos();
    this.parcelasAutomaticas = todosZerados
      || valoresAtuais.every((valor, index) => valor === valoresEsperados[index]);

    if (todosZerados) this.distribuirTotalEntreParcelas();
  }

  private calcularParcelasEmCentavos(): number[] {
    const quantidade = this.state.parcelas.length;
    if (!quantidade) return [];

    const totalEmCentavos = Math.round(this.pedidoTotal * 100);
    const valorBase = Math.floor(totalEmCentavos / quantidade);
    const restante = totalEmCentavos - valorBase * quantidade;
    return Array.from({ length: quantidade }, (_, index) =>
      valorBase + (index === quantidade - 1 ? restante : 0));
  }

  /** Carrega centro de custo e categoria (DRE) do db_financas para os selects do pedido. */
  private carregarLookupsFinancas(): void {
    this.subs.add(
      this.financasLookupService.listCentroCustoSelect().subscribe({
        next: (res) => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar os centros de custo.', AlertType.error);
            return;
          }
          this.pedidoCentroCustoOpcoes = res.list || [];
        },
        error: () => this.alertService.show('Erro', 'Não foi possível carregar os centros de custo.', AlertType.error),
      }),
    );
    this.subs.add(
      this.financasLookupService.listCategoriaFinanceiraSelect().subscribe({
        next: (res) => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar as categorias financeiras.', AlertType.error);
            return;
          }
          this.pedidoCategoriaDreOpcoes = res.list || [];
        },
        error: () => this.alertService.show('Erro', 'Não foi possível carregar as categorias financeiras.', AlertType.error),
      }),
    );
  }

  cotFmt(n: number): string {
    return numToBR(n);
  }

  // Impostos (IPI, ST, IBS, CBS) são informativos por item — não entram no total do produto.
  // Cálculos em card-edit-dialog.types (fonte única): a Aprovação exibe o mesmo total daqui.
  pedidoSubtotal(it: PedidoItemView): number {
    return calcularSubtotalItemPedido(it);
  }

  pedidoIsLowStock(it: PedidoItemView): boolean {
    return it.stock != null && it.stock < it.qty;
  }

  /** Total do pedido: itens + frete + outras despesas. */
  get pedidoTotal(): number {
    return calcularTotalPedido(this.state);
  }
}
